import { useAuth } from '@/providers/AuthProvider';
import {
  useClaimTicket,
  useJoinTicket,
  useTransferEditRights,
} from '@/hooks/api/useTicketAssignment';
import { usePermissions } from '@/hooks/usePermissions';
import { cn } from '@/lib/utils';
import {
  Loader2,
  UserPlus,
  Zap,
  ArrowRightLeft,
  ShieldOff,
  Users,
} from 'lucide-react';

interface TicketClaimBannerProps {
  ticket: any;
  permissions: ReturnType<typeof usePermissions>;
}

export const TicketClaimBanner = ({
  ticket,
  permissions,
}: TicketClaimBannerProps) => {
  const { mutate: claim, isPending: isClaiming } = useClaimTicket(ticket.id);
  const { mutate: join, isPending: isJoining } = useJoinTicket(ticket.id);
  const { mutate: transfer, isPending: isTransferring } = useTransferEditRights(
    ticket.id,
  );

  const {
    showOffShiftBanner,
    showClaimBanner,
    showJoinBanner,
    showCollabBanner,
    showTransferBanner,
    primaryAssignee,
    collaborators,
    iAmCollaborator,
  } = permissions;

  // ── Off shift — read-only, no actions ────────────────────────────────────
  if (showOffShiftBanner) {
    return (
      <Banner color="muted">
        <div className="flex items-center gap-2">
          <ShieldOff className="w-4 h-4 text-muted-foreground shrink-0" />
          <span className="text-sm text-muted-foreground">
            You are not on shift. Start your shift to claim or edit tickets.
          </span>
        </div>
      </Banner>
    );
  }

  // ── Pending claim ─────────────────────────────────────────────────────────
  if (showClaimBanner) {
    return (
      <Banner color="amber">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-amber-500 shrink-0" />
          <span className="text-sm font-medium text-foreground">
            This ticket is waiting to be claimed.
          </span>
        </div>
        <BannerButton
          onClick={() => claim()}
          loading={isClaiming}
          label="Claim Ticket"
          loadingLabel="Claiming..."
          className="bg-amber-500 hover:bg-amber-600 text-white"
        />
      </Banner>
    );
  }

  // ── Assigned, not yet joined ──────────────────────────────────────────────
  if (showJoinBanner) {
    return (
      <Banner color="blue">
        <div className="flex items-center gap-2 min-w-0">
          <UserPlus className="w-4 h-4 text-blue-500 shrink-0" />
          <span className="text-sm font-medium text-foreground">
            {primaryAssignee?.user?.name ?? 'A crew member'} is working on this
            ticket.
          </span>
        </div>
        <BannerButton
          onClick={() => join()}
          loading={isJoining}
          label="Join Ticket"
          loadingLabel="Joining..."
          className="bg-blue-500 hover:bg-blue-600 text-white"
        />
      </Banner>
    );
  }

  // ── Collaborator — read-only notice ───────────────────────────────────────
  if (showCollabBanner && iAmCollaborator) {
    return (
      <Banner color="muted">
        <div className="flex items-center gap-2 min-w-0">
          <ArrowRightLeft className="w-4 h-4 text-muted-foreground shrink-0" />
          <span className="text-sm text-muted-foreground">
            You are collaborating.{' '}
            {primaryAssignee?.user?.name ?? 'The primary assignee'} has edit
            rights.
          </span>
        </div>
      </Banner>
    );
  }

  // ── Primary with collaborators — transfer edit ────────────────────────────
  if (showTransferBanner) {
    return (
      <Banner color="muted">
        <div className="flex items-center gap-2 min-w-0">
          <Users className="w-4 h-4 text-muted-foreground shrink-0" />
          <span className="text-sm text-muted-foreground">
            {collaborators.length} collaborator
            {collaborators.length !== 1 ? 's' : ''} on this ticket.
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          {collaborators.map((c: any) => (
            <BannerButton
              key={c.id}
              onClick={() => transfer(c.userId)}
              loading={isTransferring}
              label={`Hand off to ${c.user?.name ?? 'collaborator'}`}
              loadingLabel="Transferring..."
              className="border border-border hover:bg-muted text-foreground text-xs"
            />
          ))}
        </div>
      </Banner>
    );
  }

  return null;
};

// =========================================================================
// PRIMITIVES
// =========================================================================

const colorMap = {
  amber: 'bg-amber-500/10 border-amber-500/20',
  blue: 'bg-blue-500/10  border-blue-500/20',
  muted: 'bg-muted/40     border-border',
};

const Banner = ({
  color,
  children,
}: {
  color: keyof typeof colorMap;
  children: React.ReactNode;
}) => (
  <div
    className={cn(
      'flex items-center justify-between gap-3 px-4 py-2.5 border-b',
      colorMap[color],
    )}
  >
    {children}
  </div>
);

const BannerButton = ({
  onClick,
  loading,
  label,
  loadingLabel,
  className,
}: {
  onClick: () => void;
  loading: boolean;
  label: string;
  loadingLabel: string;
  className: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={loading}
    className={cn(
      'shrink-0 h-7 px-3 text-xs font-medium rounded-full transition-all',
      'disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5',
      className,
    )}
  >
    {loading && <Loader2 className="w-3 h-3 animate-spin" />}
    {loading ? loadingLabel : label}
  </button>
);
